/**
 * Cliente frontend para edge functions de disparos (bulk sender WhatsApp).
 */

import { supabase } from "@/integrations/supabase/client";
import { getInvokeErrorText } from "@/lib/invokeError";
import type {
  CreateAdvancedDisparoPayload,
  CreateSimpleDisparoPayload,
  DisparoCampaign,
  DisparoCampaignMessage,
  DisparoCampaignStatus,
  DisparoCleanupOptions,
  DisparoMessageFilters,
  DisparoMessagePayload,
  DisparoMessageStatus,
  DisparoStatus,
  DisparosCleanupRequest,
  DisparosCleanupResponse,
  DisparosControlRequest,
  DisparosControlResponse,
  DisparosCreateAdvancedRequest,
  DisparosCreateRequest,
  DisparosCreateResponse,
  DisparosListRequest,
  DisparosListResponse,
  SenderMessageType,
} from "@/lib/disparos-types";
import { mapDisparoMessageToSenderFields } from "@/lib/disparos-interactive";
import { normalizePhoneDigits } from "@/lib/format-phone";
import { isIntegrationOffline } from "@/lib/uazapi-connection-state";
import { planDailyBatches, validateSendWindow, type DisparoSendWindow } from "@/lib/disparos-window";

export type * from "@/lib/disparos-types";

type ApiResult<T> = { data?: T; error?: string };

// ─── Low-level edge invokers ─────────────────────────────────────────────────

async function invokeDisparos<T extends { ok?: boolean; error?: string }>(
  fn: string,
  body: unknown,
): Promise<T> {
  const { data, error } = await supabase.functions.invoke(fn, { body });
  if (error) {
    const payload = data as { error?: string; message?: string } | null;
    if (payload?.error) throw new Error(payload.error);
    if (payload?.message) throw new Error(payload.message);
    throw error;
  }
  return data as T;
}

export function createDisparo(body: DisparosCreateRequest) {
  return invokeDisparos<DisparosCreateResponse>("disparos-create", body);
}

export function controlDisparo(body: DisparosControlRequest) {
  return invokeDisparos<DisparosControlResponse>("disparos-control", body);
}

export function listDisparos(body: DisparosListRequest) {
  return invokeDisparos<DisparosListResponse>("disparos-list", body);
}

function cleanupDisparoEdge(body: DisparosCleanupRequest) {
  return invokeDisparos<DisparosCleanupResponse>("disparos-cleanup", body);
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

async function safeEdgeInvoke<T extends { ok?: boolean; error?: string }>(
  fn: () => Promise<T>,
): Promise<ApiResult<T>> {
  try {
    const result = await fn();
    if (result.ok === false) {
      return { error: result.error ?? "Erro desconhecido" };
    }
    return { data: result };
  } catch (e) {
    return { error: getInvokeErrorText(e) || "Erro ao chamar API de disparos" };
  }
}

function mapMessageToCreateFields(message: DisparoMessagePayload) {
  const mapped = mapDisparoMessageToSenderFields(message);
  const {
    type,
    text,
    file,
    docName,
    choices,
    footerText,
    listButton,
    selectableCount,
    imageButton,
    carousel,
  } = mapped;
  return {
    type,
    text,
    file,
    docName,
    choices,
    footerText,
    listButton,
    selectableCount,
    imageButton,
    carousel,
  };
}

// ─── High-level adapters (useDisparos) ───────────────────────────────────────

function mapDbStatusToCampaign(status: DisparoStatus): DisparoCampaignStatus {
  const map: Record<DisparoStatus, DisparoCampaignStatus> = {
    pending: "scheduled",
    active: "running",
    paused: "paused",
    suspended: "suspended",
    completed: "completed",
    failed: "failed",
    deleted: "cancelled",
  };
  return map[status] ?? "draft";
}

function extractCampaignCounts(
  providerResponse: Record<string, unknown> | null,
  recipientCount: number,
): Pick<DisparoCampaign, "total" | "sent" | "failed" | "pending"> {
  const pr = providerResponse ?? {};
  const total = Number(pr.log_total ?? pr.logTotal ?? recipientCount) || recipientCount;
  const sent =
    Number(
      pr.log_sucess ??
        pr.log_success ??
        pr.logSuccess ??
        pr.log_delivered ??
        pr.logDelivered ??
        0,
    ) || 0;
  const failed = Number(pr.log_failed ?? pr.logFailed ?? 0) || 0;
  const pending = Math.max(0, total - sent - failed);
  return { total, sent, failed, pending };
}

function extractFolderId(providerResponse: Record<string, unknown> | null | undefined): string | null {
  if (!providerResponse) return null;
  const candidates = [
    providerResponse.folder_id,
    providerResponse.folderId,
    providerResponse.id,
  ];
  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) return candidate.trim();
    if (typeof candidate === "number") return String(candidate);
  }
  return null;
}

function mapProviderFolderStatusToDb(
  folder: Record<string, unknown>,
  currentDbStatus: DisparoStatus,
): DisparoStatus | null {
  const providerStatus = String(folder.status ?? "").toLowerCase();
  if (providerStatus === "done") return "completed";
  if (providerStatus === "paused") return "paused";
  if (providerStatus === "sending") return "active";
  if (providerStatus === "scheduled") return "pending";
  if (providerStatus === "deleting") return "deleted";

  const total = Number(folder.log_total ?? folder.logTotal ?? 0);
  const sent =
    Number(
      folder.log_sucess ??
        folder.log_success ??
        folder.logSuccess ??
        folder.log_delivered ??
        folder.logDelivered ??
        0,
    ) || 0;
  const failed = Number(folder.log_failed ?? folder.logFailed ?? 0) || 0;
  if (total > 0 && sent + failed >= total && currentDbStatus === "active") {
    return "completed";
  }

  return null;
}

function mapProviderMessageStatus(status: unknown): DisparoMessageStatus {
  const s = String(status ?? "").toLowerCase();
  if (s.includes("fail") || s.includes("error")) return "failed";
  if (s.includes("read") || s.includes("played")) return "read";
  if (s.includes("deliver")) return "delivered";
  if (s.includes("sent") || s.includes("server_ack") || s.includes("succes") || s.includes("sucess")) return "sent";
  if (s.includes("queue")) return "queued";
  if (s.includes("skip")) return "skipped";
  return "pending";
}

function phonesMatch(a: string, b: string): boolean {
  const da = normalizePhoneDigits(a);
  const db = normalizePhoneDigits(b);
  if (!da || !db) return false;
  if (da === db) return true;
  const stripCountry = (digits: string) =>
    digits.startsWith("55") && digits.length > 11 ? digits.slice(2) : digits;
  return stripCountry(da) === stripCountry(db);
}

function parseProviderTimestamp(value: unknown): string | null {
  if (value == null || value === "") return null;
  if (typeof value === "number" && Number.isFinite(value)) {
    const ms = value > 1e12 ? value : value * 1000;
    return new Date(ms).toISOString();
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    if (/^\d+$/.test(trimmed)) {
      const n = Number(trimmed);
      const ms = n > 1e12 ? n : n * 1000;
      return new Date(ms).toISOString();
    }
    const parsed = new Date(trimmed);
    if (!Number.isNaN(parsed.getTime())) return parsed.toISOString();
  }
  return null;
}

function extractProviderPhone(row: Record<string, unknown>): string {
  const candidates = [
    row.chatid,
    row.chatId,
    row.number,
    row.phone,
    row.to,
    row.Chat,
    row.sender_pn,
    row.senderPn,
    row.jid,
    row.remoteJid,
    row.remote_jid,
    row.recipient,
    row.wa_id,
    row.waId,
    row.sender,
    row.Sender,
  ];
  for (const candidate of candidates) {
    if (candidate == null) continue;
    const raw = String(candidate).trim();
    if (!raw) continue;
    const digits = normalizePhoneDigits(raw.split("@")[0]);
    if (digits.length >= 10) return digits;
  }
  return "";
}

function extractProviderLeadName(row: Record<string, unknown>): string | null {
  for (const key of [
    "name",
    "lead_name",
    "senderName",
    "pushName",
    "push_name",
    "contactName",
    "contact_name",
  ]) {
    const value = row[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return null;
}

function extractProviderSentAt(row: Record<string, unknown>): string | null {
  for (const key of [
    "sent_at",
    "sentAt",
    "delivered_at",
    "deliveredAt",
    "updated_at",
    "updatedAt",
    "created_at",
    "createdAt",
    "timestamp",
    "date",
    "messageTimestamp",
    "message_timestamp",
  ]) {
    const parsed = parseProviderTimestamp(row[key]);
    if (parsed) return parsed;
  }
  return null;
}

function extractProviderError(row: Record<string, unknown>): string | null {
  for (const key of ["error", "error_message", "errorMessage", "failReason", "fail_reason", "reason"]) {
    const value = row[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  const response = row.response;
  if (response && typeof response === "object") {
    const nested = response as Record<string, unknown>;
    if (typeof nested.message === "string" && nested.message.trim()) return nested.message.trim();
    if (Array.isArray(nested.message) && nested.message.length > 0) {
      return String(nested.message[0]).trim();
    }
  }
  const status = mapProviderMessageStatus(row.status ?? row.messageStatus);
  const info = row.info;
  if (status === "failed" && typeof info === "string" && info.trim()) return info.trim();
  return null;
}

function mapDbDisparoMessageStatus(status: string): DisparoMessageStatus {
  const s = status.toLowerCase();
  if (s === "queued") return "queued";
  if (s === "pending") return "pending";
  if (s === "sent") return "sent";
  if (s === "delivered") return "delivered";
  if (s === "read") return "read";
  if (s === "failed") return "failed";
  if (s === "skipped") return "skipped";
  return mapProviderMessageStatus(status);
}

type DisparoMessageDbRow = {
  id: string;
  disparo_id: string;
  lead_id: string | null;
  phone: string;
  recipient_name: string | null;
  status: string;
  error: string | null;
  sent_at: string | null;
  created_at: string;
};

function mapDbMessageToCampaignMessage(row: DisparoMessageDbRow): DisparoCampaignMessage {
  return {
    id: row.id,
    campaign_id: row.disparo_id,
    lead_id: row.lead_id,
    phone: row.phone,
    lead_name: row.recipient_name,
    status: mapDbDisparoMessageStatus(row.status),
    error: row.error,
    sent_at: row.sent_at,
    created_at: row.created_at,
  };
}

function mapCampaignMessageToDbStatus(status: DisparoMessageStatus): string {
  if (status === "pending") return "queued";
  return status;
}

async function fetchDisparoMessagesFromDb(
  campaignId: string,
): Promise<DisparoMessageDbRow[]> {
  const { data, error } = await supabase
    .from("disparo_messages")
    .select(
      "id, disparo_id, lead_id, phone, recipient_name, status, error, sent_at, created_at",
    )
    .eq("disparo_id", campaignId)
    .order("created_at", { ascending: true });

  if (error) throw new Error(error.message);
  return (data ?? []) as DisparoMessageDbRow[];
}

async function upsertDisparoMessagesFromProvider(
  workspaceId: string,
  campaignId: string,
  messages: DisparoCampaignMessage[],
): Promise<void> {
  if (messages.length === 0) return;

  const rows = messages.map((message) => ({
    disparo_id: campaignId,
    workspace_id: workspaceId,
    lead_id: message.lead_id,
    phone: normalizePhoneDigits(message.phone),
    recipient_name: message.lead_name,
    status: mapCampaignMessageToDbStatus(message.status),
    error: message.error,
    sent_at: message.sent_at,
  }));

  const CHUNK = 200;
  for (let i = 0; i < rows.length; i += CHUNK) {
    const { error } = await supabase
      .from("disparo_messages")
      .upsert(rows.slice(i, i + CHUNK), { onConflict: "disparo_id,phone" });
    if (error) {
      console.warn("[disparos-api] upsert disparo_messages failed:", error.message);
      return;
    }
  }
}

function mapProviderMessage(row: Record<string, unknown>, campaignId: string): DisparoCampaignMessage {
  const phone = extractProviderPhone(row);
  const sentAt = extractProviderSentAt(row);
  const createdAt =
    parseProviderTimestamp(row.created_at ?? row.createdAt) ?? sentAt ?? new Date().toISOString();
  return {
    id: String(
      row.id ??
        row.message_id ??
        row.messageid ??
        row.messageId ??
        `${campaignId}-${phone || createdAt}`,
    ),
    campaign_id: campaignId,
    lead_id: row.lead_id ? String(row.lead_id) : null,
    phone,
    lead_name: extractProviderLeadName(row),
    status: mapProviderMessageStatus(row.status ?? row.messageStatus),
    error: extractProviderError(row),
    sent_at: sentAt,
    created_at: createdAt,
  };
}

function extractProviderMessageRows(data: unknown): unknown[] {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== "object") return [];
  const obj = data as Record<string, unknown>;
  for (const key of ["messages", "logs", "list", "data", "items"]) {
    const value = obj[key];
    if (Array.isArray(value)) return value;
  }
  return [];
}

type RecipientContextRow = { phone: string; leadId: string | null; name?: string | null };

function parseRecipientContext(
  config: unknown,
  phones: string[],
  leadIds: string[],
): RecipientContextRow[] {
  if (config && typeof config === "object") {
    const recipients = (config as Record<string, unknown>).recipients;
    if (Array.isArray(recipients) && recipients.length > 0) {
      return recipients
        .map((entry): RecipientContextRow | null => {
          if (!entry || typeof entry !== "object") return null;
          const row = entry as Record<string, unknown>;
          const phone = normalizePhoneDigits(String(row.phone ?? ""));
          if (!phone) return null;
          const leadId = row.lead_id ? String(row.lead_id) : null;
          const name =
            typeof row.name === "string" && row.name.trim() ? row.name.trim() : null;
          return { phone, leadId, name };
        })
        .filter((row): row is RecipientContextRow => row !== null);
    }
  }

  return phones.map((phone, index) => ({
    phone: normalizePhoneDigits(phone),
    leadId: leadIds[index] ?? null,
    name: null,
  }));
}

function phoneLookupVariants(phone: string): string[] {
  const digits = normalizePhoneDigits(phone);
  if (!digits) return [];
  const variants = new Set<string>([digits]);
  if (digits.startsWith("55") && digits.length > 11) variants.add(digits.slice(2));
  if (digits.length >= 9) variants.add(digits.slice(-9));
  return [...variants];
}

async function lookupLeadsByPhones(
  workspaceId: string,
  phones: string[],
): Promise<Map<string, { id: string; name: string | null; phone: string | null }>> {
  const wanted = [...new Set(phones.map((phone) => normalizePhoneDigits(phone)).filter(Boolean))];
  if (wanted.length === 0) return new Map();

  const variants = [...new Set(wanted.flatMap(phoneLookupVariants))];
  const byPhone = new Map<string, { id: string; name: string | null; phone: string | null }>();

  const CHUNK = 40;
  for (let i = 0; i < variants.length; i += CHUNK) {
    const chunk = variants.slice(i, i + CHUNK);
    const orFilter = chunk.map((variant) => `phone.ilike.%${variant}`).join(",");

    const { data: leads } = await supabase
      .from("leads")
      .select("id, name, phone")
      .eq("workspace_id", workspaceId)
      .not("phone", "is", null)
      .or(orFilter);

    for (const lead of leads ?? []) {
      const digits = normalizePhoneDigits(lead.phone);
      if (!digits) continue;
      for (const phone of wanted) {
        if (phonesMatch(digits, phone) && !byPhone.has(phone)) {
          byPhone.set(phone, lead);
        }
      }
    }
  }

  return byPhone;
}

async function enrichCampaignMessages(
  messages: DisparoCampaignMessage[],
  ctx: {
    workspaceId: string;
    phones: string[];
    leadIds: (string | null)[];
    config?: unknown;
  },
): Promise<DisparoCampaignMessage[]> {
  const recipients = parseRecipientContext(ctx.config, ctx.phones, ctx.leadIds);

  const recipientByPhone = new Map<string, RecipientContextRow>();
  for (const recipient of recipients) {
    recipientByPhone.set(recipient.phone, recipient);
  }

  const resolveRecipient = (phone: string): RecipientContextRow | null => {
    const digits = normalizePhoneDigits(phone);
    if (!digits) return null;
    if (recipientByPhone.has(digits)) return recipientByPhone.get(digits)!;
    for (const [key, recipient] of recipientByPhone) {
      if (phonesMatch(key, digits)) return recipient;
    }
    return null;
  };

  const leadIdsNeeded = new Set<string>();
  for (const recipient of recipients) {
    if (recipient.leadId) leadIdsNeeded.add(recipient.leadId);
  }
  for (const message of messages) {
    if (message.lead_id) leadIdsNeeded.add(message.lead_id);
    else if (message.phone) {
      const recipient = resolveRecipient(message.phone);
      if (recipient?.leadId) leadIdsNeeded.add(recipient.leadId);
    }
  }

  const leadById = new Map<string, { id: string; name: string | null; phone: string | null }>();
  const leadByPhone = new Map<string, { id: string; name: string | null; phone: string | null }>();

  if (leadIdsNeeded.size > 0) {
    const ids = [...leadIdsNeeded];
    const LEAD_CHUNK = 100;
    for (let i = 0; i < ids.length; i += LEAD_CHUNK) {
      const chunk = ids.slice(i, i + LEAD_CHUNK);
      const { data: leads } = await supabase
        .from("leads")
        .select("id, name, phone")
        .in("id", chunk);

      for (const lead of leads ?? []) {
        leadById.set(lead.id, lead);
        const digits = normalizePhoneDigits(lead.phone);
        if (digits) leadByPhone.set(digits, lead);
      }
    }
  }

  const phonesNeedingLookup = new Set<string>();
  for (const recipient of recipients) {
    if (!recipient.leadId && !recipient.name) {
      phonesNeedingLookup.add(recipient.phone);
    }
  }
  for (const message of messages) {
    if (message.lead_name) continue;
    const phone = message.phone;
    if (!phone) continue;
    const recipient = resolveRecipient(phone);
    if (recipient?.name) continue;
    if (recipient?.leadId && leadById.has(recipient.leadId)) continue;
    if (message.lead_id && leadById.has(message.lead_id)) continue;
    phonesNeedingLookup.add(phone);
  }

  if (phonesNeedingLookup.size > 0) {
    const lookedUp = await lookupLeadsByPhones(ctx.workspaceId, [...phonesNeedingLookup]);
    for (const [phone, lead] of lookedUp) {
      leadByPhone.set(phone, lead);
      leadById.set(lead.id, lead);
    }
  }

  const findLeadByPhone = (phone: string) => {
    const digits = normalizePhoneDigits(phone);
    if (!digits) return null;
    if (leadByPhone.has(digits)) return leadByPhone.get(digits)!;
    for (const [key, lead] of leadByPhone) {
      if (phonesMatch(key, digits)) return lead;
    }
    const recipient = resolveRecipient(phone);
    if (recipient?.leadId) return leadById.get(recipient.leadId) ?? null;
    return null;
  };

  const canUseIndexFallback =
    recipients.length > 0 &&
    messages.length === recipients.length &&
    messages.every((message) => !message.phone);

  return messages.map((message, index) => {
    let phone = message.phone;
    let leadId = message.lead_id;
    let leadName = message.lead_name;

    if (!phone && canUseIndexFallback) {
      phone = recipients[index]?.phone ?? "";
      leadId = leadId ?? recipients[index]?.leadId ?? null;
      leadName = leadName ?? recipients[index]?.name ?? null;
    }

    if (phone) {
      const recipient = resolveRecipient(phone);
      if (!leadId && recipient?.leadId) leadId = recipient.leadId;
      if (!leadName && recipient?.name) leadName = recipient.name;
    }

    const lead = leadId ? (leadById.get(leadId) ?? null) : findLeadByPhone(phone);
    if (lead) {
      leadId = leadId ?? lead.id;
      leadName = leadName ?? lead.name;
      if (!phone) phone = normalizePhoneDigits(lead.phone);
    } else if (!leadName && leadId) {
      leadName = leadById.get(leadId)?.name ?? null;
    }

    return { ...message, phone, lead_id: leadId, lead_name: leadName };
  });
}

function parseListMessagesData(
  data: unknown,
  campaignId: string,
  filters?: DisparoMessageFilters,
): { messages: DisparoCampaignMessage[]; total: number } {
  const raw = extractProviderMessageRows(data);

  let messages = raw
    .filter((item): item is Record<string, unknown> => !!item && typeof item === "object")
    .map((item) => mapProviderMessage(item, campaignId));

  if (filters?.status && filters.status !== "all") {
    messages = messages.filter((m) => m.status === filters.status);
  }

  if (filters?.search?.trim()) {
    const q = filters.search.trim().toLowerCase();
    messages = messages.filter(
      (m) => m.phone.includes(q) || (m.lead_name?.toLowerCase().includes(q) ?? false),
    );
  }

  const total = extractMessageListTotal(data, messages.length);

  return { messages, total };
}

function extractMessageListTotal(data: unknown, fallback: number): number {
  if (!data || typeof data !== "object") return fallback;
  const obj = data as Record<string, unknown>;
  if ("total" in obj && Number(obj.total)) return Number(obj.total);
  const pagination = obj.pagination;
  if (pagination && typeof pagination === "object") {
    const totalRecords = (pagination as Record<string, unknown>).totalRecords;
    if (Number(totalRecords)) return Number(totalRecords);
  }
  return fallback;
}

function extractDeletedCount(provider: unknown): number {
  if (!provider || typeof provider !== "object") return 0;
  const p = provider as Record<string, unknown>;
  return Number(p.deleted ?? p.removed ?? p.count ?? 0) || 0;
}

async function fetchProviderFolders(
  workspaceId: string,
  integrationId: string,
): Promise<Map<string, Record<string, unknown>>> {
  const folderMap = new Map<string, Record<string, unknown>>();

  for (const status of ["Active", "Archived"] as const) {
    const result = await safeEdgeInvoke(() =>
      listDisparos({
        action: "listfolders",
        workspace_id: workspaceId,
        integration_id: integrationId,
        status,
      }),
    );
    if (result.error) continue;

    const folders = Array.isArray(result.data?.data) ? result.data.data : [];
    for (const folder of folders) {
      if (!folder || typeof folder !== "object") continue;
      const row = folder as Record<string, unknown>;
      const id = String(row.id ?? row.folder_id ?? "");
      if (id) folderMap.set(id, row);
    }
  }

  return folderMap;
}

/** Cache em memória de listfolders por integração (TTL 60s) — evita UAZAPI repetido ao expandir campanhas. */
const folderSyncCache = new Map<
  string,
  { fetchedAt: number; folders: Map<string, Record<string, unknown>> }
>();
const FOLDER_CACHE_TTL_MS = 60_000;

async function getProviderFoldersCached(
  workspaceId: string,
  integrationId: string,
): Promise<Map<string, Record<string, unknown>>> {
  const key = `${workspaceId}:${integrationId}`;
  const cached = folderSyncCache.get(key);
  if (cached && Date.now() - cached.fetchedAt < FOLDER_CACHE_TTL_MS) {
    return cached.folders;
  }
  const folders = await fetchProviderFolders(workspaceId, integrationId);
  folderSyncCache.set(key, { fetchedAt: Date.now(), folders });
  return folders;
}

type DisparoCampaignRow = {
  id: string;
  name: string | null;
  status: DisparoStatus;
  integration_id: string;
  folder_id: string | null;
  recipient_count: number;
  provider_response: Record<string, unknown> | null;
  scheduled_for: string | null;
  created_at: string;
  updated_at: string;
};

/** Estado operacional das conexões usadas pelas campanhas (1 select). */
async function fetchOfflineIntegrationIds(
  workspaceId: string,
  integrationIds: string[],
): Promise<Set<string>> {
  const offline = new Set<string>();
  if (integrationIds.length === 0) return offline;

  const { data, error } = await supabase
    .from("integrations")
    .select("id, status, config")
    .eq("workspace_id", workspaceId)
    .in("id", integrationIds);

  if (error || !data) return offline;

  const found = new Set<string>();
  for (const row of data as Array<{ id: string; status: string | null; config: unknown }>) {
    found.add(row.id);
    if (isIntegrationOffline(row)) offline.add(row.id);
  }
  // Conexão removida = não há canal para enviar.
  for (const id of integrationIds) {
    if (!found.has(id)) offline.add(id);
  }

  return offline;
}

async function syncCampaignRowsWithProvider(
  workspaceId: string,
  rows: DisparoCampaignRow[],
): Promise<DisparoCampaignRow[]> {
  const syncable = rows.filter(
    (row) =>
      row.folder_id &&
      row.status !== "deleted" &&
      row.status !== "completed" &&
      row.status !== "failed" &&
      row.status !== "suspended",
  );
  if (syncable.length === 0) return rows;

  const byIntegration = new Map<string, DisparoCampaignRow[]>();
  for (const row of syncable) {
    const list = byIntegration.get(row.integration_id) ?? [];
    list.push(row);
    byIntegration.set(row.integration_id, list);
  }

  const syncedRows = [...rows];

  // Conexão caída: campanha agendada/em andamento é suspensa e parada no provedor,
  // para não disparar sozinha quando o número voltar. Só sai daí por reagendamento.
  const offlineIntegrations = await fetchOfflineIntegrationIds(workspaceId, [...byIntegration.keys()]);

  for (const integrationId of offlineIntegrations) {
    const campaigns = byIntegration.get(integrationId) ?? [];
    byIntegration.delete(integrationId);

    for (const campaign of campaigns) {
      if (campaign.status !== "pending" && campaign.status !== "active") continue;

      const suspension = {
        reason: "integration_disconnected",
        suspended_at: new Date().toISOString(),
        previous_status: campaign.status,
        previous_scheduled_for: campaign.scheduled_for,
        integration_id: campaign.integration_id,
      };
      const mergedProvider = {
        ...(campaign.provider_response ?? {}),
        __suspension: suspension,
      } as Record<string, unknown>;

      // Só marca "Suspensa" quando o provedor confirma que parou. Se a parada falhar,
      // a campanha pode continuar enviando — mostrar "Suspensa" seria mentir ao usuário
      // e abriria um reagendamento que duplica envios.
      if (!campaign.folder_id) continue;
      const stopped = await safeEdgeInvoke(() =>
        controlDisparo({
          workspace_id: workspaceId,
          integration_id: campaign.integration_id,
          folder_id: campaign.folder_id!,
          action: "stop",
        }),
      );
      if (stopped.error || stopped.data?.ok === false) {
        console.warn("[disparos] conexão parece offline, mas o provedor não confirmou a parada; mantendo status", {
          campaign: campaign.id,
          error: stopped.error,
        });
        continue;
      }

      await supabase
        .from("disparos")
        .update({ status: "suspended", provider_response: mergedProvider as never })
        .eq("id", campaign.id);

      const index = syncedRows.findIndex((row) => row.id === campaign.id);
      if (index >= 0) {
        syncedRows[index] = {
          ...syncedRows[index],
          status: "suspended",
          provider_response: mergedProvider,
        };
      }
    }
  }

  for (const [integrationId, campaigns] of byIntegration) {
    const folderMap = await getProviderFoldersCached(workspaceId, integrationId);

    for (const campaign of campaigns) {
      const folderId = campaign.folder_id!;
      const folder = folderMap.get(folderId);
      const providerData = folder ?? campaign.provider_response ?? {};
      if (!folder && !campaign.provider_response) continue;

      const mergedProvider = { ...(campaign.provider_response ?? {}), ...(folder ?? {}) } as Record<string, unknown> as never;
      const nextStatus = mapProviderFolderStatusToDb(providerData, campaign.status);
      const index = syncedRows.findIndex((row) => row.id === campaign.id);
      if (index < 0) continue;

      syncedRows[index] = {
        ...syncedRows[index],
        provider_response: mergedProvider,
        status: nextStatus ?? syncedRows[index].status,
      };

      // Persiste apenas quando algo mudou (status ou contadores): 1 UPDATE por campanha,
      // e os números continuam visíveis se o provedor ficar indisponível depois.
      const before = extractCampaignCounts(campaign.provider_response, campaign.recipient_count);
      const after = extractCampaignCounts(mergedProvider, campaign.recipient_count);
      const countsChanged =
        before.total !== after.total ||
        before.sent !== after.sent ||
        before.failed !== after.failed;

      if (folder && ((nextStatus && nextStatus !== campaign.status) || countsChanged)) {
        await supabase
          .from("disparos")
          .update({
            ...(nextStatus && nextStatus !== campaign.status ? { status: nextStatus } : {}),
            provider_response: mergedProvider,
          })
          .eq("id", campaign.id);
      }
    }
  }

  return syncedRows;
}

const MESSAGES_PAGE_SIZE = 100;
const MESSAGES_MAX_OFFSET = 5_000;

async function fetchAllProviderMessages(
  workspaceId: string,
  integrationId: string,
  folderId: string,
  campaignId: string,
): Promise<{ messages: DisparoCampaignMessage[]; total: number }> {
  let offset = 0;
  const allMessages: DisparoCampaignMessage[] = [];
  let total = 0;

  while (true) {
    const result = await safeEdgeInvoke(() =>
      listDisparos({
        action: "listmessages",
        workspace_id: workspaceId,
        integration_id: integrationId,
        folder_id: folderId,
        limit: MESSAGES_PAGE_SIZE,
        offset,
      }),
    );

    if (result.error) {
      if (allMessages.length > 0) break;
      throw new Error(result.error);
    }

    const parsed = parseListMessagesData(result.data?.data, campaignId);
    allMessages.push(...parsed.messages);
    total = Math.max(parsed.total, allMessages.length);

    if (parsed.messages.length < MESSAGES_PAGE_SIZE || allMessages.length >= total) break;
    offset += MESSAGES_PAGE_SIZE;
    if (offset >= MESSAGES_MAX_OFFSET) break;
  }

  return { messages: allMessages, total };
}

async function markDisparoDeleted(workspaceId: string, campaignId: string): Promise<ApiResult<{ ok: boolean }>> {
  const { error } = await supabase
    .from("disparos")
    .update({ status: "deleted" })
    .eq("id", campaignId)
    .eq("workspace_id", workspaceId);

  if (error) return { error: error.message };
  return { data: { ok: true } };
}

async function getFirstUazapiIntegration(workspaceId: string): Promise<{ id: string } | null> {
  const { data, error } = await supabase
    .from("integrations")
    .select("id")
    .eq("workspace_id", workspaceId)
    .eq("provider", "whatsapp_uazapi")
    .eq("status", "active")
    .order("created_at", { ascending: true })
    .limit(1)
    .maybeSingle();

  if (error || !data) return null;
  return data as { id: string };
}

// ─── High-level adapters (useDisparos) ───────────────────────────────────────

/** Divide em lotes diários quando há período de envio; senão, lote único. */
async function createInBatches<T>(
  items: T[],
  opts: {
    name: string;
    window?: DisparoSendWindow | null;
    delayMaxSeconds?: number;
    scheduledAt?: string | null;
  },
  build: (batch: T[], name: string, scheduledFor: string | undefined) => DisparosCreateRequest,
): Promise<ApiResult<{ campaign_id: string; batches?: number }>> {
  let batches: Array<{ scheduledAt: Date | null; items: T[] }>;
  if (opts.window) {
    const invalid = validateSendWindow(opts.window);
    if (invalid) return { error: invalid };
    batches = planDailyBatches(items, opts.window, {
      delayMaxSeconds: opts.delayMaxSeconds ?? 8,
      startAt: opts.scheduledAt ? new Date(opts.scheduledAt) : null,
    });
  } else {
    batches = [{ scheduledAt: opts.scheduledAt ? new Date(opts.scheduledAt) : null, items }];
  }

  let firstId: string | null = null;
  for (let i = 0; i < batches.length; i++) {
    const b = batches[i];
    const name = batches.length > 1 ? `${opts.name} (dia ${i + 1}/${batches.length})` : opts.name;
    const result = await safeEdgeInvoke(() =>
      createDisparo(build(b.items, name, b.scheduledAt ? b.scheduledAt.toISOString() : undefined)),
    );
    if (result.error || !result.data?.disparo_id) {
      const base = result.error ?? "Disparo criado sem ID";
      return {
        error: i === 0 ? base : `${i} de ${batches.length} dias foram agendados; os demais falharam: ${base}`,
      };
    }
    firstId ??= result.data.disparo_id;
  }
  return { data: { campaign_id: firstId!, batches: batches.length } };
}

export async function createSimpleDisparo(
  payload: CreateSimpleDisparoPayload,
): Promise<ApiResult<{ campaign_id: string; batches?: number }>> {
  const fields = mapMessageToCreateFields(payload.message);
  return createInBatches(
    payload.recipients,
    {
      name: payload.campaign_name,
      window: payload.send_window,
      delayMaxSeconds: payload.delay_max_seconds,
      scheduledAt: payload.scheduled_at,
    },
    (batch, name, scheduledFor) => ({
      workspace_id: payload.workspace_id,
      integration_id: payload.integration_id,
      mode: "simple",
      name,
      phones: batch.map((r) => r.phone),
      lead_ids: batch.map((r) => r.lead_id),
      names: batch.map((r) => r.name),
      ...fields,
      delayMin: payload.delay_min_seconds,
      delayMax: payload.delay_max_seconds,
      scheduled_for: scheduledFor,
    }),
  );
}

export async function createAdvancedDisparo(
  payload: CreateAdvancedDisparoPayload,
): Promise<ApiResult<{ campaign_id: string; batches?: number }>> {
  return createInBatches(
    payload.recipients,
    {
      name: payload.campaign_name,
      window: payload.send_window,
      delayMaxSeconds: payload.delay_max_seconds,
      scheduledAt: payload.scheduled_at,
    },
    (batch, name, scheduledFor) => ({
      workspace_id: payload.workspace_id,
      integration_id: payload.integration_id,
      mode: "advanced",
      name,
      messages: batch.map((r) => ({
        phone: r.phone,
        lead_id: r.lead_id,
        name: r.name,
        ...mapMessageToCreateFields(r.message),
      })),
      delayMin: payload.delay_min_seconds,
      delayMax: payload.delay_max_seconds,
      scheduled_for: scheduledFor,
    }),
  );
}

export async function listDisparoCampaigns(
  workspaceId: string,
  options?: { syncProvider?: boolean },
): Promise<ApiResult<{ campaigns: DisparoCampaign[] }>> {
  const { data, error } = await supabase
    .from("disparos")
    .select(
      "id, name, status, integration_id, folder_id, recipient_count, provider_response, scheduled_for, created_at, updated_at",
    )
    .eq("workspace_id", workspaceId)
    .neq("status", "deleted")
    .order("created_at", { ascending: false });

  if (error) {
    return { error: error.message };
  }

  const rows = (data ?? []) as DisparoCampaignRow[];
  const syncedRows = options?.syncProvider
    ? await syncCampaignRowsWithProvider(workspaceId, rows)
    : rows;

  const campaigns: DisparoCampaign[] = syncedRows.map((r) => {
    const counts = extractCampaignCounts(r.provider_response, r.recipient_count);
    return {
      id: r.id,
      name: r.name ?? "Sem nome",
      status: mapDbStatusToCampaign(r.status),
      integration_id: r.integration_id,
      scheduled_at: r.scheduled_for,
      created_at: r.created_at,
      updated_at: r.updated_at,
      suspended_reason:
        r.status === "suspended"
          ? String(
              (r.provider_response?.__suspension as Record<string, unknown> | undefined)?.reason ??
                "integration_disconnected",
            )
          : null,
      ...counts,
    };
  });

  return { data: { campaigns } };
}

/** Sincroniza status/contagens de UMA campanha com o provedor (ao expandir). Usa cache de listfolders 60s. */
export async function syncDisparoCampaignProvider(
  workspaceId: string,
  campaignId: string,
): Promise<ApiResult<{ ok: boolean }>> {
  const { data: row, error: fetchError } = await supabase
    .from("disparos")
    .select("id, status, integration_id, folder_id, provider_response")
    .eq("id", campaignId)
    .eq("workspace_id", workspaceId)
    .maybeSingle();

  if (fetchError) return { error: fetchError.message };
  if (!row) return { error: "Campanha não encontrada" };

  const campaign = row as {
    id: string;
    status: DisparoStatus;
    integration_id: string;
    folder_id: string | null;
    provider_response: Record<string, unknown> | null;
  };

  if (
    !campaign.folder_id ||
    campaign.status === "deleted" ||
    campaign.status === "completed" ||
    campaign.status === "failed"
  ) {
    return { data: { ok: true } };
  }

  const folderMap = await getProviderFoldersCached(workspaceId, campaign.integration_id);
  const folder = folderMap.get(campaign.folder_id);
  if (!folder) return { data: { ok: true } };

  const mergedProvider = { ...(campaign.provider_response ?? {}), ...folder } as Record<string, unknown> as never;
  const nextStatus = mapProviderFolderStatusToDb(folder, campaign.status);

  if (nextStatus && nextStatus !== campaign.status) {
    const { error: updateError } = await supabase
      .from("disparos")
      .update({
        status: nextStatus,
        provider_response: mergedProvider,
      })
      .eq("id", campaign.id);

    if (updateError) return { error: updateError.message };
  }

  return { data: { ok: true } };
}

export async function controlDisparoCampaign(
  workspaceId: string,
  campaignId: string,
  action: "pause" | "resume" | "delete",
): Promise<ApiResult<{ ok: boolean }>> {
  const { data: row, error: fetchError } = await supabase
    .from("disparos")
    .select("folder_id, integration_id, provider_response")
    .eq("id", campaignId)
    .eq("workspace_id", workspaceId)
    .maybeSingle();

  if (fetchError) return { error: fetchError.message };
  if (!row) return { error: "Campanha não encontrada" };

  const disparo = row as {
    folder_id: string | null;
    integration_id: string;
    provider_response: Record<string, unknown> | null;
  };

  if (action === "delete") {
    const folderId =
      disparo.folder_id ?? extractFolderId(disparo.provider_response ?? undefined);
    if (!folderId) {
      return markDisparoDeleted(workspaceId, campaignId);
    }

    const result = await safeEdgeInvoke(() =>
      controlDisparo({
        workspace_id: workspaceId,
        integration_id: disparo.integration_id,
        folder_id: folderId,
        action: "delete",
        disparo_id: campaignId,
      }),
    );

    if (result.error) {
      return markDisparoDeleted(workspaceId, campaignId);
    }
    return { data: { ok: true } };
  }

  const folderId =
    disparo.folder_id ?? extractFolderId(disparo.provider_response ?? undefined);
  if (!folderId) return { error: "Campanha sem pasta no provedor" };

  const edgeAction = action === "pause" ? "stop" : "continue";

  const result = await safeEdgeInvoke(() =>
    controlDisparo({
      workspace_id: workspaceId,
      integration_id: disparo.integration_id,
      folder_id: folderId,
      action: edgeAction,
      disparo_id: campaignId,
    }),
  );

  if (result.error) return { error: result.error };
  return { data: { ok: true } };
}

export async function syncDisparoCampaignMessagesFromProvider(
  workspaceId: string,
  campaignId: string,
): Promise<ApiResult<{ messages: DisparoCampaignMessage[]; total: number }>> {
  const { data: row, error: fetchError } = await supabase
    .from("disparos")
    .select("folder_id, integration_id, phones, lead_ids, config, provider_response")
    .eq("id", campaignId)
    .eq("workspace_id", workspaceId)
    .maybeSingle();

  if (fetchError) return { error: fetchError.message };
  if (!row) return { error: "Campanha não encontrada" };

  const disparo = row as {
    folder_id: string | null;
    integration_id: string;
    phones: string[] | null;
    lead_ids: string[] | null;
    config: unknown;
    provider_response: Record<string, unknown> | null;
  };

  const folderId =
    disparo.folder_id ?? extractFolderId(disparo.provider_response ?? undefined);
  if (!folderId) return { error: "Campanha sem pasta no provedor" };

  await syncDisparoCampaignProvider(workspaceId, campaignId);

  try {
    const parsed = await fetchAllProviderMessages(
      workspaceId,
      disparo.integration_id,
      folderId,
      campaignId,
    );
    const messages = await enrichCampaignMessages(parsed.messages, {
      workspaceId,
      phones: disparo.phones ?? [],
      leadIds: disparo.lead_ids ?? [],
      config: disparo.config,
    });

    await upsertDisparoMessagesFromProvider(workspaceId, campaignId, messages);

    return { data: { messages, total: parsed.total } };
  } catch (e) {
    return { error: getInvokeErrorText(e) || "Erro ao sincronizar mensagens" };
  }
}

export async function listDisparoCampaignMessages(
  workspaceId: string,
  campaignId: string,
  options?: { forceProvider?: boolean },
): Promise<ApiResult<{ messages: DisparoCampaignMessage[]; total: number }>> {
  const { data: row, error: fetchError } = await supabase
    .from("disparos")
    .select("folder_id, integration_id, phones, lead_ids, config, provider_response")
    .eq("id", campaignId)
    .eq("workspace_id", workspaceId)
    .maybeSingle();

  if (fetchError) return { error: fetchError.message };
  if (!row) return { error: "Campanha não encontrada" };

  const disparo = row as {
    folder_id: string | null;
    integration_id: string;
    phones: string[] | null;
    lead_ids: string[] | null;
    config: unknown;
    provider_response: Record<string, unknown> | null;
  };

  const enrichCtx = {
    workspaceId,
    phones: disparo.phones ?? [],
    leadIds: disparo.lead_ids ?? [],
    config: disparo.config,
  };

  if (!options?.forceProvider) {
    try {
      const dbRows = await fetchDisparoMessagesFromDb(campaignId);
      // Se todas as linhas ainda estão no estado inicial (nenhum retorno do provedor chegou
      // via webhook), consulta o provedor para trazer enviados/falhas reais.
      const allStillQueued =
        dbRows.length > 0 &&
        dbRows.every((r) => {
          const s = String((r as { status?: string | null }).status ?? "queued");
          return s === "queued" || s === "pending";
        });
      if (dbRows.length > 0 && !allStillQueued) {
        const messages = await enrichCampaignMessages(
          dbRows.map(mapDbMessageToCampaignMessage),
          enrichCtx,
        );
        return { data: { messages, total: dbRows.length } };
      }
    } catch (e) {
      return { error: getInvokeErrorText(e) || "Erro ao carregar mensagens" };
    }
  }

  const folderId =
    disparo.folder_id ?? extractFolderId(disparo.provider_response ?? undefined);
  if (!folderId) return { data: { messages: [], total: 0 } };

  try {
    const parsed = await fetchAllProviderMessages(
      workspaceId,
      disparo.integration_id,
      folderId,
      campaignId,
    );
    const messages = await enrichCampaignMessages(parsed.messages, enrichCtx);
    await upsertDisparoMessagesFromProvider(workspaceId, campaignId, messages);
    return { data: { messages, total: parsed.total } };
  } catch (e) {
    return { error: getInvokeErrorText(e) || "Erro ao listar mensagens" };
  }
}

export async function cleanupDisparos(
  workspaceId: string,
  options: DisparoCleanupOptions,
): Promise<ApiResult<{ deleted: number }>> {
  const integration = await getFirstUazapiIntegration(workspaceId);
  if (!integration) {
    return { error: "Nenhuma conexão WhatsApp ativa encontrada" };
  }

  let deleted = 0;

  if (options.delete_sent || options.delete_failed || options.older_than_days) {
    const hours = options.older_than_days ? options.older_than_days * 24 : 168;
    const result = await safeEdgeInvoke(() =>
      cleanupDisparoEdge({
        workspace_id: workspaceId,
        integration_id: integration.id,
        action: "cleardone",
        hours,
      }),
    );
    if (result.error) return { error: result.error };
    deleted += extractDeletedCount(result.data?.provider);
  }

  if (options.delete_pending) {
    const result = await safeEdgeInvoke(() =>
      cleanupDisparoEdge({
        workspace_id: workspaceId,
        integration_id: integration.id,
        action: "clearall",
      }),
    );
    if (result.error) return { error: result.error };
    deleted += extractDeletedCount(result.data?.provider);
  }

  return { data: { deleted } };
}

// ─── Reagendamento de campanha suspensa ──────────────────────────────────────

type SuspendedRecipient = { phone: string; lead_id?: string | null; name?: string | null };

/** Telefones que já receberam a mensagem — não entram na nova campanha. */
async function fetchAlreadySentPhones(campaignId: string): Promise<Set<string>> {
  const sent = new Set<string>();
  const { data, error } = await supabase
    .from("disparo_messages")
    .select("phone, status")
    .eq("disparo_id", campaignId)
    .in("status", ["sent", "delivered", "read"]);

  if (error || !data) return sent;
  for (const row of data as Array<{ phone: string | null }>) {
    if (row.phone) sent.add(normalizePhoneDigits(row.phone));
  }
  return sent;
}

/**
 * Reagenda uma campanha suspensa: cria uma nova campanha no canal escolhido, apenas com
 * os destinatários ainda não enviados, e encerra a antiga no provedor.
 */
export async function rescheduleDisparoCampaign(
  workspaceId: string,
  campaignId: string,
  payload: { integration_id: string; scheduled_at: string },
): Promise<ApiResult<{ campaign_id: string; recipient_count: number }>> {
  if (!payload.integration_id) return { error: "Selecione a conexão que fará o envio" };
  const scheduledDate = new Date(payload.scheduled_at);
  if (Number.isNaN(scheduledDate.getTime()) || scheduledDate.getTime() <= Date.now()) {
    return { error: "Escolha uma data e hora futuras" };
  }

  const { data: integrationRow, error: integrationError } = await supabase
    .from("integrations")
    .select("id, status, config")
    .eq("id", payload.integration_id)
    .eq("workspace_id", workspaceId)
    .maybeSingle();

  if (integrationError) return { error: integrationError.message };
  if (!integrationRow || isIntegrationOffline(integrationRow)) {
    return { error: "Essa conexão não está conectada. Reconecte em Integrações." };
  }

  const { data: row, error: fetchError } = await supabase
    .from("disparos")
    .select("id, name, mode, status, integration_id, folder_id, phones, lead_ids, config, provider_response")
    .eq("id", campaignId)
    .eq("workspace_id", workspaceId)
    .maybeSingle();

  if (fetchError) return { error: fetchError.message };
  if (!row) return { error: "Campanha não encontrada" };

  const campaign = row as {
    id: string;
    name: string | null;
    mode: "simple" | "advanced";
    status: DisparoStatus;
    integration_id: string;
    folder_id: string | null;
    phones: string[] | null;
    lead_ids: string[] | null;
    config: Record<string, unknown> | null;
    provider_response: Record<string, unknown> | null;
  };

  const config = (campaign.config ?? {}) as Record<string, unknown>;
  const messageConfig = (config.message_config ?? {}) as Record<string, unknown>;
  const delayMin = typeof config.delay_min === "number" ? config.delay_min : undefined;
  const delayMax = typeof config.delay_max === "number" ? config.delay_max : undefined;

  const configRecipients: SuspendedRecipient[] = Array.isArray(config.recipients)
    ? (config.recipients as SuspendedRecipient[])
    : (campaign.phones ?? []).map((phone, i) => ({
        phone,
        lead_id: campaign.lead_ids?.[i] ?? null,
        name: null,
      }));

  // 1) Garante que a campanha antiga está parada ANTES de montar a nova — senão as duas enviam.
  const oldFolderIdForStop =
    campaign.folder_id ?? extractFolderId(campaign.provider_response ?? undefined);
  if (oldFolderIdForStop) {
    const stopped = await safeEdgeInvoke(() =>
      controlDisparo({
        workspace_id: workspaceId,
        integration_id: campaign.integration_id,
        folder_id: oldFolderIdForStop,
        action: "stop",
      }),
    );
    if (stopped.error || stopped.data?.ok === false) {
      return {
        error:
          "Não foi possível confirmar que a campanha antiga parou. Nada foi reagendado para evitar mensagens repetidas.",
      };
    }

    // 2) Lê do provedor quem já recebeu. Sem isso, o reagendamento reenviaria para todos.
    const synced = await syncDisparoCampaignMessagesFromProvider(workspaceId, campaignId);
    if (synced.error) {
      return {
        error:
          "Não foi possível conferir quem já recebeu esta campanha. Tente de novo em alguns minutos.",
      };
    }
  }

  const sentPhones = await fetchAlreadySentPhones(campaignId);
  const pending = configRecipients.filter(
    (recipient) => recipient?.phone && !sentPhones.has(normalizePhoneDigits(recipient.phone)),
  );

  if (pending.length === 0) {
    return { error: "Não há destinatários pendentes nesta campanha" };
  }

  const baseName = campaign.name?.trim() || "Disparo";
  const newName = /\(reagendad/i.test(baseName) ? baseName : `${baseName} (reagendado)`;

  let body: DisparosCreateRequest;

  if (campaign.mode === "advanced") {
    const originalMessages = Array.isArray(messageConfig.messages)
      ? (messageConfig.messages as Array<Record<string, unknown>>)
      : [];
    const pendingPhones = new Set(pending.map((r) => normalizePhoneDigits(r.phone)));
    const messages = originalMessages.filter((m) => {
      const phone = typeof m.phone === "string" ? m.phone : "";
      return phone && pendingPhones.has(normalizePhoneDigits(phone));
    });

    if (messages.length === 0) {
      return { error: "Não foi possível recuperar as mensagens desta campanha" };
    }

    body = {
      workspace_id: workspaceId,
      integration_id: payload.integration_id,
      mode: "advanced",
      name: newName,
      messages: messages as unknown as DisparosCreateAdvancedRequest["messages"],
      delayMin,
      delayMax,
      scheduled_for: scheduledDate.toISOString(),
    };
  } else {
    const type = messageConfig.type;
    if (typeof type !== "string") {
      return { error: "Não foi possível recuperar a mensagem desta campanha" };
    }

    body = {
      workspace_id: workspaceId,
      integration_id: payload.integration_id,
      mode: "simple",
      name: newName,
      phones: pending.map((r) => r.phone),
      lead_ids: pending.map((r) => r.lead_id ?? ""),
      names: pending.map((r) => r.name ?? ""),
      type: type as SenderMessageType,
      text: messageConfig.text as string | undefined,
      file: messageConfig.file as string | undefined,
      docName: messageConfig.docName as string | undefined,
      choices: messageConfig.choices as string[] | undefined,
      footerText: messageConfig.footerText as string | undefined,
      listButton: messageConfig.listButton as string | undefined,
      selectableCount: messageConfig.selectableCount as number | undefined,
      imageButton: messageConfig.imageButton as string | undefined,
      carousel: messageConfig.carousel,
      delayMin,
      delayMax,
      scheduled_for: scheduledDate.toISOString(),
    };
  }

  const created = await safeEdgeInvoke(() => createDisparo(body));
  if (created.error) return { error: created.error };
  const newCampaignId = created.data?.disparo_id;
  if (!newCampaignId) return { error: "Disparo reagendado sem ID" };

  // Encerra a campanha antiga no provedor (best-effort) e marca como cancelada.
  const oldFolderId =
    campaign.folder_id ?? extractFolderId(campaign.provider_response ?? undefined);
  if (oldFolderId) {
    await safeEdgeInvoke(() =>
      controlDisparo({
        workspace_id: workspaceId,
        integration_id: campaign.integration_id,
        folder_id: oldFolderId,
        action: "delete",
      }),
    );
  }

  const suspension = {
    ...((campaign.provider_response?.__suspension as Record<string, unknown> | undefined) ?? {}),
    rescheduled_to: newCampaignId,
    rescheduled_at: new Date().toISOString(),
    rescheduled_integration_id: payload.integration_id,
  };

  await supabase
    .from("disparos")
    .update({
      status: "deleted",
      provider_response: {
        ...(campaign.provider_response ?? {}),
        __suspension: suspension,
      } as never,
    })
    .eq("id", campaignId)
    .eq("workspace_id", workspaceId);

  return { data: { campaign_id: newCampaignId, recipient_count: pending.length } };
}
